import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { AppRow } from '../../layout/appRow';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { Task } from '../../models/task';
import { getEmployees } from '../../services/api/employeeServices';
import { addUsersToTask } from '../../services/api/taskServices';
import { AppButton } from '../app/button/appButton';
import { AppButtonContained } from '../app/button/appButtonContained';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';

import UserBox from './taskUsersItem';
import { NO_USERS, PLEASE_TYPE } from '../../constants';
import { generateUserWithIdLabel } from '../../services/employeeService';

interface UsersProp {
  onClose(): void;
  task: Task | null;
  handleUpdateTaskUsers(): void;
}

const style = {
  dialogActions: {
    p: 0,
    mt: 2,
  },
  appRow: {
    display: 'flex',
    alignItems: 'center',
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    flexGrow: 1,
    p: 1,
    m: 1,
  },
  taskName: {
    ml: 3,
    fontWeight: 600,
    display: '-webkit-box',
    ' -webkit-box-orient': 'vertical',
    ' -webkit-line-clamp': 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
} as const;

export default function TaskUsersDialog({
  onClose,
  task,
  handleUpdateTaskUsers,
}: UsersProp): JSX.Element {
  const [userList, setUserList] = useState<AutocompleteOption[]>([]);
  const [users, setUsers] = useState<AutocompleteOption[]>([]);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  const methods = useForm();

  useEffect(() => {
    if (task) {
      const activeAndInactiveEmployees = task.employees.map((user) => ({
        id: user.id,
        label: generateUserWithIdLabel(user.name, user.internalId),
      }));

      setUserList(activeAndInactiveEmployees);
    }
  }, [task]);

  const onResetSearchUsers = useCallback(() => {
    setIsLoadingUsers(true);
    getEmployees({ take: 10, includeInactive: true })
      .then((resp) => {
        setUsers(
          resp.items.map((user) => ({
            id: user.id,
            label: generateUserWithIdLabel(user.name, user.internalId),
          }))
        );
      })
      .finally(() => setIsLoadingUsers(false));
  }, []);

  const watchField = methods.watch('user');

  const onSubmit = useCallback(
    (usersOnTask) => {
      delete usersOnTask.user;

      setIsLoadingRequest(true);
      const usersAsArray = Object.keys(usersOnTask).map((key) => ({
        isActive: usersOnTask[key],
        employeesId: key,
      }));

      if (task) {
        addUsersToTask(task?.id, usersAsArray)
          .then(() => {
            handleUpdateTaskUsers();
          })
          .then(() =>
            setTimeout(() => {
              onClose();
            }, 500)
          )
          .finally(() => setIsLoadingRequest(false));
      }
    },
    [task, onClose, handleUpdateTaskUsers]
  );

  const onAddUser = useCallback(() => {
    if (!watchField) return;

    if (userList.some((user) => user.id === watchField.id)) return;
    if (!userList.some((user) => user.id === watchField.id)) {
      setUserList([
        ...userList,
        { label: watchField.label, id: watchField.id },
      ]);
    }

    methods.resetField('user');
    onResetSearchUsers();
  }, [methods, onResetSearchUsers, userList, watchField]);

  const onSearchOption = useCallback((value) => {
    setIsLoadingUsers(true);
    getEmployees({ take: 10, filter: value, includeInactive: true })
      .then((resp) => {
        setUsers(
          resp.items.map((user) => ({
            id: user.id,
            label: generateUserWithIdLabel(user.name, user.internalId),
          }))
        );
      })
      .finally(() => setIsLoadingUsers(false));
  }, []);

  const checkIfUserIsActiveOnTask = useCallback(
    (user) => {
      const isOnTask = task?.employees.some((employee) => {
        return employee.id === user.id;
      });

      const isActiveOnTask = task?.employees.some((employee) => {
        return employee.id === user.id && employee.isActiveOnTask;
      });

      const isInUserList = userList.some((employee) => {
        return employee.id === user.id;
      });

      if (isActiveOnTask) {
        return true;
      } else if (!isOnTask && isInUserList) {
        return true;
      }

      return false;
    },
    [task, userList]
  );

  return (
    <Dialog open sx={{ borderRadius: '5px' }}>
      <AppDialogTitle title="Add users to task" handleClose={onClose} />
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
          autoComplete="off"
        >
          <DialogContent sx={{ pb: 0 }}>
            <Typography sx={style.taskName}>Task: {task?.name}</Typography>
            <AppRow sx={style.appRow}>
              <Box sx={style.box}>
                <AppControlledAutocomplete
                  name="user"
                  label="Users"
                  options={users}
                  searchOptions={onSearchOption}
                  isLoading={isLoadingUsers}
                  noOptionsText={NO_USERS}
                  placeholder={PLEASE_TYPE}
                />
              </Box>
              <AppButtonContained
                label={'Add selected user'}
                handleClick={onAddUser}
                isIconButton
              />
            </AppRow>
            <Typography sx={{ ml: 3 }}>
              Users added: {userList.length}
            </Typography>

            <Container sx={{ overflowY: 'scroll', maxHeight: '360px' }}>
              <Grid
                container
                justifyContent="flex-start"
                alignItems="center"
                sx={{ mt: 0 }}
                spacing={3}
              >
                {userList.map((user) => (
                  <UserBox
                    key={user.id}
                    name={user.id}
                    label={user.label}
                    defaultValue={checkIfUserIsActiveOnTask(user)}
                  />
                ))}
              </Grid>
            </Container>
          </DialogContent>
          <DialogActions sx={style.dialogActions}>
            <AppButton label="Cancel" onClick={onClose} />
            <AppButton
              label="Save"
              isPrimary
              buttonType="submit"
              isLoading={isLoadingRequest}
              disabled={!userList.length}
            />
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
